import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  CognitoUserPool,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';
import { Header, Card, Button, Input, CrownedELXR } from './components/ui';
import { tokens } from './styles/tokens';

// Debug environment variables
console.log('SignUpPage Environment variables:', {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
  Region: process.env.REACT_APP_AWS_REGION
});

// Cognito pool setup
const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
};

console.log('SignUpPage Pool data:', poolData);

function SignUpPage() {
  const [signUpData, setSignUpData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    walletAddress: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Create user pool instance only if we have the required values
  let userPool = null;
  if (poolData.UserPoolId && poolData.ClientId) {
    userPool = new CognitoUserPool(poolData);
  } else {
    console.error('SignUpPage - Missing required Cognito configuration:', poolData);
  }

  const handleChange = (e) => {
    setSignUpData({ ...signUpData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!signUpData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signUpData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!signUpData.password) {
      newErrors.password = 'Password is required';
    } else if (signUpData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (!signUpData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (signUpData.password !== signUpData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!signUpData.firstName) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!signUpData.lastName) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!signUpData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    }
    
    if (!signUpData.walletAddress) {
      newErrors.walletAddress = 'XRP wallet address is required';
    } else if (!/^r[a-zA-Z0-9]{24,34}$/.test(signUpData.walletAddress)) {
      newErrors.walletAddress = 'Invalid XRP wallet address format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!userPool) {
      setErrors({ general: 'Configuration error. Please try again later.' });
      return;
    }
    
    setLoading(true);
    
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: signUpData.email }),
      new CognitoUserAttribute({ Name: 'given_name', Value: signUpData.firstName }),
      new CognitoUserAttribute({ Name: 'family_name', Value: signUpData.lastName }),
      new CognitoUserAttribute({ Name: 'birthdate', Value: signUpData.dateOfBirth }),
      new CognitoUserAttribute({ Name: 'custom:wallet_address', Value: signUpData.walletAddress })
    ];
    
    userPool.signUp(
      signUpData.email,
      signUpData.password,
      attributeList,
      null,
      (err, result) => {
        setLoading(false);
        
        if (err) {
          console.error('SignUp error:', err);
          if (err.code === 'UsernameExistsException') {
            setErrors({ general: 'An account with this email already exists' });
          } else if (err.code === 'InvalidPasswordException') {
            setErrors({ password: 'Password does not meet requirements. Must contain uppercase, lowercase, numbers, and special characters.' });
          } else {
            setErrors({ general: err.message || 'Failed to create account' });
          }
          return;
        }
        
        console.log('SignUp success:', result);
        navigate('/verify-email', { 
          state: { 
            email: signUpData.email,
            message: 'Account created successfully! Please check your email for the verification code.'
          } 
        });
      }
    );
  };

  const menuItems = [
    { label: 'Home', href: '/home' },
    { label: 'Sports', href: '/sports' },
    { label: 'My Bets', href: '/my-bets' }
  ];

  return (
    <div style={{ minHeight: '100vh', background: tokens.colors.dark[900] }}>
      <Header logo="ELXR Sportsbook" menuItems={menuItems} />
      
      <section style={{
        background: `linear-gradient(135deg, ${tokens.colors.dark[800]} 0%, ${tokens.colors.dark[900]} 100%)`,
        minHeight: 'calc(100vh - 4rem)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${tokens.spacing[8]} 0`,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle at 20% 80%, ${tokens.colors.primary[900]}22 0%, transparent 50%),
                           radial-gradient(circle at 80% 20%, ${tokens.colors.secondary[900]}22 0%, transparent 50%)`,
          opacity: 0.5
        }} />
        
        <div style={{ 
          maxWidth: '600px',
          width: '100%',
          position: 'relative',
          zIndex: 1
        }}>
          <Card variant="elevated" padding="large">
            <Card.Body>
              <h1 style={{ 
                fontSize: tokens.typography.fontSize['3xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                background: tokens.colors.gradients.glow,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: tokens.spacing[2],
                textAlign: 'center'
              }}>
                Create Your Account
              </h1>
              <p style={{ 
                textAlign: 'center',
                marginBottom: tokens.spacing[8],
                color: tokens.colors.neutral[400],
                fontSize: tokens.typography.fontSize.lg
              }}>
                Join <CrownedELXR /> Sportsbook and start betting with XRP
              </p>
              
              {errors.general && (
                <div style={{
                  background: `${tokens.colors.error[900]}33`,
                  border: `1px solid ${tokens.colors.error[700]}`,
                  borderRadius: tokens.borderRadius.md,
                  padding: tokens.spacing[3],
                  marginBottom: tokens.spacing[6],
                  color: tokens.colors.error[400],
                  fontSize: tokens.typography.fontSize.sm
                }}>
                  {errors.general}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div style={{ 
                  display: 'grid',
                  gap: tokens.spacing[4]
                }}>
                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: tokens.spacing[4]
                  }}>
                    <Input
                      type="text"
                      name="firstName"
                      label="First Name"
                      placeholder="John"
                      value={signUpData.firstName}
                      onChange={handleChange}
                      error={errors.firstName}
                      disabled={loading}
                      required
                      fullWidth
                    />
                    
                    <Input
                      type="text"
                      name="lastName"
                      label="Last Name"
                      placeholder="Doe"
                      value={signUpData.lastName}
                      onChange={handleChange}
                      error={errors.lastName}
                      disabled={loading}
                      required
                      fullWidth
                    />
                  </div>
                  
                  <Input
                    type="email"
                    name="email"
                    label="Email"
                    placeholder="you@example.com"
                    value={signUpData.email}
                    onChange={handleChange}
                    error={errors.email}
                    disabled={loading}
                    required
                    fullWidth
                    autoComplete="email"
                  />
                  
                  <Input
                    type="date"
                    name="dateOfBirth"
                    label="Date of Birth"
                    value={signUpData.dateOfBirth}
                    onChange={handleChange}
                    error={errors.dateOfBirth}
                    disabled={loading}
                    required
                    fullWidth
                  />
                  
                  <Input
                    type="text"
                    name="walletAddress"
                    label="XRP Wallet Address"
                    placeholder="rXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                    value={signUpData.walletAddress}
                    onChange={handleChange}
                    error={errors.walletAddress}
                    disabled={loading}
                    required
                    fullWidth
                    helperText="Your XRP wallet address for receiving payouts"
                  />
                  
                  <Input
                    type="password"
                    name="password"
                    label="Password"
                    placeholder="••••••••"
                    value={signUpData.password}
                    onChange={handleChange}
                    error={errors.password}
                    disabled={loading}
                    required
                    fullWidth
                    autoComplete="new-password"
                    helperText="At least 8 characters with uppercase, lowercase, numbers, and symbols"
                  />
                  
                  <Input
                    type="password"
                    name="confirmPassword"
                    label="Confirm Password"
                    placeholder="••••••••"
                    value={signUpData.confirmPassword}
                    onChange={handleChange}
                    error={errors.confirmPassword}
                    disabled={loading}
                    required
                    fullWidth
                    autoComplete="new-password"
                  />
                </div>
                
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                  loading={loading}
                  disabled={loading}
                  style={{ marginTop: tokens.spacing[8] }}
                >
                  Create Account
                </Button>
                
                <p style={{ 
                  textAlign: 'center',
                  marginTop: tokens.spacing[6],
                  color: tokens.colors.neutral[400]
                }}>
                  Already have an account?{' '}
                  <Link 
                    to="/"
                    style={{
                      color: tokens.colors.primary[400],
                      fontWeight: tokens.typography.fontWeight.medium,
                      textDecoration: 'none'
                    }}
                  >
                    Sign In
                  </Link>
                </p>
              </form>
            </Card.Body>
          </Card>
        </div>
      </section>
    </div>
  );
}

export default SignUpPage; 